import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiUser, FiMail, FiMapPin, FiEdit3, FiSave, FiX, FiSettings, 
  FiAward, FiActivity, FiFlag, FiShield, FiHeart, FiBook, 
  FiTarget, FiTrendingUp, FiDollarSign, FiUsers, FiCalendar,
  FiMessageCircle, FiCheckSquare, FiTwitter, FiFacebook, 
  FiInstagram, FiGlobe, FiLinkedin, FiYoutube, FiCheck, FiCamera
} = FiIcons;

const Profile = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    bio: 'Passionate conservative fighting for traditional American values, limited government, and the American dream. Proud supporter of the Republican movement since 2016.',
    location: 'Austin, TX',
    interests: ['Constitutional Rights', 'Free Markets', 'Second Amendment', 'Religious Liberty', 'Secure Borders', 'Pro-Life', 'MAGA'],
    role: user?.role || 'user',
    socialMedia: {
      twitter: '@patriot_defender',
      facebook: 'patriot.defender',
      instagram: 'patriot.defender',
      linkedin: 'patriot-defender',
      youtube: '',
      website: ''
    }
  });

  // Enhanced achievements with levels and richer data
  const achievements = [
    {
      id: 1,
      title: 'Learning Champion',
      description: 'Completed 10+ constitutional learning modules',
      icon: FiBook,
      color: 'text-yellow-500',
      level: 3,
      maxLevel: 3,
      unlocked: true,
      date: '2023-11-15',
      featured: true
    },
    {
      id: 2,
      title: 'Community Builder',
      description: 'Joined 5+ conservative groups and made 10+ meaningful contributions',
      icon: FiUsers,
      color: 'text-blue-500',
      level: 2,
      maxLevel: 3,
      unlocked: true,
      date: '2023-12-01'
    },
    {
      id: 3,
      title: 'Event Attendee',
      description: 'Attended 3+ Republican events and rallies',
      icon: FiCalendar,
      color: 'text-green-500',
      level: 2,
      maxLevel: 3,
      unlocked: true,
      date: '2024-01-05'
    },
    {
      id: 4,
      title: 'Constitution Defender',
      description: 'Completed Constitution 101 course with perfect score',
      icon: FiShield,
      color: 'text-patriot-red',
      level: 3,
      maxLevel: 3,
      unlocked: true,
      date: '2023-07-04',
      featured: true
    },
    {
      id: 5,
      title: 'Patriot Donor',
      description: 'Supported conservative candidates and causes',
      icon: FiDollarSign,
      color: 'text-patriot-gold',
      level: 1,
      maxLevel: 3,
      unlocked: true,
      date: '2024-02-10'
    },
    {
      id: 6,
      title: 'America First Advocate',
      description: 'Shared 20+ platform resources with your network',
      icon: FiFlag,
      color: 'text-patriot-navy',
      level: 3,
      maxLevel: 3,
      unlocked: true,
      date: '2024-01-20',
      featured: true
    },
    {
      id: 7,
      title: 'Debate Champion',
      description: 'Won 5+ forum debates with facts and logic',
      icon: FiMessageCircle,
      color: 'text-indigo-500',
      level: 2,
      maxLevel: 3,
      unlocked: true,
      date: '2024-02-15'
    },
    {
      id: 8,
      title: 'Election Volunteer',
      description: 'Volunteered for Republican campaigns and GOTV efforts',
      icon: FiCheckSquare,
      color: 'text-purple-500',
      level: 2,
      maxLevel: 3,
      unlocked: true,
      date: '2023-10-12'
    },
    {
      id: 9,
      title: 'Conservative Media Supporter',
      description: 'Subscribed to and regularly shared Republican news sources',
      icon: FiGlobe,
      color: 'text-teal-500',
      level: 1,
      maxLevel: 3,
      unlocked: true,
      date: '2024-03-01'
    },
    {
      id: 10,
      title: 'Second Amendment Defender',
      description: 'Completed firearms safety course and advocated for gun rights',
      icon: FiTarget,
      color: 'text-red-700',
      level: 3,
      maxLevel: 3,
      unlocked: true,
      date: '2023-09-25',
      featured: true
    }
  ];

  // More realistic activity stats
  const activityStats = [
    { label: 'Constitutional Modules Completed', value: 23, total: 30, icon: FiBook },
    { label: 'GOP Events Attended', value: 15, total: 20, icon: FiCalendar },
    { label: 'Conservative Groups Joined', value: 7, total: 10, icon: FiUsers },
    { label: 'Forum Discussions Participated', value: 42, total: 50, icon: FiMessageCircle },
    { label: 'Resources Shared', value: 37, total: 50, icon: FiFlag },
    { label: 'Candidate Support Actions', value: 18, total: 25, icon: FiTarget },
    { label: 'Platform Engagement Score', value: 91, total: 100, icon: FiActivity }
  ];

  // Expanded Republican interest categories
  const availableInterests = [
    'Constitutional Rights',
    'Second Amendment',
    'Free Markets',
    'Limited Government',
    'Religious Liberty',
    'Secure Borders',
    'Election Integrity',
    'America First',
    'Traditional Values',
    'Pro-Life',
    'Energy Independence',
    'School Choice',
    'Fiscal Responsibility',
    'Military Support',
    'Law Enforcement',
    'Anti-Socialism',
    'Patriotic Education',
    'Gun Rights',
    'Tax Reform',
    'Deregulation',
    'National Defense',
    'Judicial Originalism',
    'Conservative Media',
    'Family Values',
    'Rural America',
    'Faith-Based Initiatives',
    'Economic Growth',
    'American Exceptionalism',
    'Voter ID Laws',
    'Strong Military',
    'Healthcare Freedom',
    'Welfare Reform',
    'Small Business Support',
    'Right to Work',
    'Veteran Support',
    'Patriotism',
    'MAGA Movement',
    'American Heritage',
    'Anti-Communism',
    'Property Rights',
    'Republican Party History',
    'Conservative Thought Leaders',
    'Federalism',
    'Free Speech',
    'Religious Freedom',
    'Anti-Woke',
    'Parents Rights',
    'Conservative Campus Activism',
    'Republican Women',
    'Young Republicans',
    'Freedom Caucus',
    'Tea Party',
  ];

  // User's groups
  const userGroups = [
    { 
      id: 1, 
      name: 'Texas Constitutional Defenders', 
      members: 1247, 
      role: 'Member',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=100&h=100&fit=crop',
      lastActive: '2 days ago'
    },
    { 
      id: 2, 
      name: 'Second Amendment Advocates', 
      members: 3456, 
      role: 'Moderator',
      image: 'https://images.unsplash.com/photo-1553977669-705d630ab08f?w=100&h=100&fit=crop',
      lastActive: 'Yesterday'
    },
    { 
      id: 3, 
      name: 'Young Conservatives of Austin', 
      members: 872, 
      role: 'Member',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=100&h=100&fit=crop',
      lastActive: '3 days ago'
    },
  ];

  // Recent events attended
  const recentEvents = [
    {
      id: 1,
      title: 'Conservative Leadership Summit',
      date: '2024-03-15',
      location: 'Dallas, TX',
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=100&h=100&fit=crop'
    },
    {
      id: 2,
      title: 'Second Amendment Rally',
      date: '2024-02-28',
      location: 'Austin, TX',
      image: 'https://images.unsplash.com/photo-1553977669-705d630ab08f?w=100&h=100&fit=crop'
    },
    {
      id: 3,
      title: 'Republican Fundraiser Gala',
      date: '2024-02-15',
      location: 'Houston, TX',
      image: 'https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=100&h=100&fit=crop'
    }
  ];

  const handleSave = () => {
    // Save profile data
    setIsEditing(false);
  };

  const handleCancel = () => {
    // Reset form data
    setIsEditing(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSocialMediaChange = (e) => {
    setFormData({
      ...formData,
      socialMedia: {
        ...formData.socialMedia,
        [e.target.name]: e.target.value
      }
    });
  };

  const addInterest = () => {
    const newInterest = prompt('Enter a new interest:');
    if (newInterest && !formData.interests.includes(newInterest)) {
      setFormData({ ...formData, interests: [...formData.interests, newInterest] });
    }
  };

  const removeInterest = (interest) => {
    setFormData({ ...formData, interests: formData.interests.filter(i => i !== interest) });
  };

  const getBadgeStyle = (level, maxLevel) => {
    if (level === maxLevel) return "bg-patriot-gold text-patriot-navy";
    if (level === 2) return "bg-patriot-red text-white";
    return "bg-gray-200 text-gray-700";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const calculateCompletionPercentage = () => {
    // Calculate profile completion percentage
    let total = 0;
    let completed = 0;
    
    // Basic info
    if (formData.name) completed++;
    if (formData.email) completed++;
    if (formData.bio) completed++;
    if (formData.location) completed++;
    total += 4;
    
    // Interests
    if (formData.interests.length > 0) {
      completed += Math.min(formData.interests.length, 3) / 3;
    }
    total += 1;
    
    // Social media
    Object.values(formData.socialMedia).forEach(value => {
      if (value) completed++;
    });
    total += Object.keys(formData.socialMedia).length;
    
    return Math.round((completed / total) * 100);
  };

  const profileCompletionPercentage = calculateCompletionPercentage();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-lg shadow-md overflow-hidden mb-8"
        >
          <div className="bg-gradient-to-r from-patriot-navy via-patriot-red to-patriot-navy h-40 relative">
            <div className="absolute inset-0 pattern-stars opacity-20"></div>
            {isEditing && (
              <button className="absolute bottom-2 right-2 bg-white bg-opacity-80 p-2 rounded-full hover:bg-opacity-100 transition-all">
                <SafeIcon icon={FiCamera} className="h-5 w-5 text-patriot-navy" />
              </button>
            )}
          </div>
          <div className="px-6 pb-6">
            <div className="flex flex-col md:flex-row md:items-end -mt-16 mb-4">
              <div className="relative">
                <img 
                  src={user?.avatar || "https://ui-avatars.com/api/?name=Republican+Patriot&background=DC2626&color=fff&size=200"} 
                  alt={user?.name || "Profile"} 
                  className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
                />
                {isEditing && (
                  <button className="absolute bottom-1 right-1 bg-white p-1.5 rounded-full shadow hover:bg-gray-100 transition-all">
                    <SafeIcon icon={FiCamera} className="h-4 w-4 text-patriot-navy" />
                  </button>
                )}
              </div>
              <div className="md:ml-6 mt-4 md:mt-0 flex-1">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-patriot-navy">{formData.name}</h1>
                    <p className="text-gray-600 capitalize flex items-center">
                      <span>{formData.role}</span>
                      <span className="mx-2">•</span>
                      <SafeIcon icon={FiMapPin} className="h-3.5 w-3.5 text-gray-500 mr-1" />
                      <span className="text-gray-600">{formData.location}</span>
                    </p>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <button
                      onClick={() => setIsEditing(!isEditing)}
                      className="flex items-center px-4 py-2 bg-patriot-red text-white rounded-lg hover:bg-patriot-red-dark transition-colors duration-200"
                    >
                      <SafeIcon icon={isEditing ? FiX : FiEdit3} className="h-4 w-4 mr-2" />
                      {isEditing ? 'Cancel' : 'Edit Profile'}
                    </button>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-3">
                  {achievements.filter(a => a.featured).slice(0, 3).map(achievement => (
                    <span key={achievement.id} className="inline-flex items-center px-2 py-1 bg-patriot-gold bg-opacity-10 text-patriot-navy border border-patriot-gold rounded-full text-xs font-medium">
                      <SafeIcon icon={achievement.icon} className={`h-3 w-3 mr-1 ${achievement.color}`} />
                      {achievement.title}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Profile Navigation */}
          <div className="border-t border-gray-200 px-6">
            <nav className="flex overflow-x-auto hide-scrollbar">
              <button
                onClick={() => setActiveTab('profile')}
                className={`px-4 py-3 font-medium text-sm border-b-2 whitespace-nowrap ${
                  activeTab === 'profile' ? 'border-patriot-red text-patriot-red' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={FiUser} className="h-4 w-4 inline mr-2" />
                Profile
              </button>
              <button
                onClick={() => setActiveTab('achievements')}
                className={`px-4 py-3 font-medium text-sm border-b-2 whitespace-nowrap ${
                  activeTab === 'achievements' ? 'border-patriot-red text-patriot-red' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={FiAward} className="h-4 w-4 inline mr-2" />
                Achievements
              </button>
              <button
                onClick={() => setActiveTab('activity')}
                className={`px-4 py-3 font-medium text-sm border-b-2 whitespace-nowrap ${
                  activeTab === 'activity' ? 'border-patriot-red text-patriot-red' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={FiActivity} className="h-4 w-4 inline mr-2" />
                Activity
              </button>
              <button
                onClick={() => setActiveTab('groups')}
                className={`px-4 py-3 font-medium text-sm border-b-2 whitespace-nowrap ${
                  activeTab === 'groups' ? 'border-patriot-red text-patriot-red' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={FiUsers} className="h-4 w-4 inline mr-2" />
                Groups
              </button>
            </nav>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className={`lg:col-span-2 space-y-6 ${activeTab !== 'profile' && 'lg:col-span-3'}`}>
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <>
                {/* Basic Info */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="bg-white rounded-lg shadow-md p-6"
                >
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-patriot-navy">Profile Information</h2>
                    {isEditing && (
                      <button
                        onClick={handleSave}
                        className="flex items-center px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 text-sm"
                      >
                        <SafeIcon icon={FiSave} className="h-4 w-4 mr-1" />
                        Save
                      </button>
                    )}
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                        />
                      ) : (
                        <div className="flex items-center">
                          <SafeIcon icon={FiUser} className="h-4 w-4 text-gray-400 mr-2" />
                          <span>{formData.name}</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      {isEditing ? (
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                        />
                      ) : (
                        <div className="flex items-center">
                          <SafeIcon icon={FiMail} className="h-4 w-4 text-gray-400 mr-2" />
                          <span>{formData.email}</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                        />
                      ) : (
                        <div className="flex items-center">
                          <SafeIcon icon={FiMapPin} className="h-4 w-4 text-gray-400 mr-2" />
                          <span>{formData.location}</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                      {isEditing ? (
                        <textarea
                          name="bio"
                          value={formData.bio}
                          onChange={handleChange}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                        />
                      ) : (
                        <p className="text-gray-600 leading-relaxed">{formData.bio}</p>
                      )}
                    </div>
                  </div>
                </motion.div>

                {/* Social Media */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.15 }}
                  className="bg-white rounded-lg shadow-md p-6"
                >
                  <h2 className="text-xl font-semibold text-patriot-navy mb-6">Social Media</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <SafeIcon icon={FiTwitter} className="h-4 w-4 inline mr-1 text-blue-400" />
                        Twitter
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="twitter"
                          value={formData.socialMedia.twitter}
                          onChange={handleSocialMediaChange}
                          placeholder="@username"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                        />
                      ) : (
                        formData.socialMedia.twitter ? (
                          <a href={`https://twitter.com/${formData.socialMedia.twitter.replace('@', '')}`} 
                             target="_blank" rel="noopener noreferrer"
                             className="text-blue-500 hover:underline">
                            {formData.socialMedia.twitter}
                          </a>
                        ) : (
                          <span className="text-gray-400 italic">Not provided</span>
                        )
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <SafeIcon icon={FiFacebook} className="h-4 w-4 inline mr-1 text-blue-600" />
                        Facebook
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="facebook"
                          value={formData.socialMedia.facebook}
                          onChange={handleSocialMediaChange}
                          placeholder="username"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                        />
                      ) : (
                        formData.socialMedia.facebook ? (
                          <a href={`https://facebook.com/${formData.socialMedia.facebook}`} 
                             target="_blank" rel="noopener noreferrer"
                             className="text-blue-600 hover:underline">
                            {formData.socialMedia.facebook}
                          </a>
                        ) : (
                          <span className="text-gray-400 italic">Not provided</span>
                        )
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <SafeIcon icon={FiInstagram} className="h-4 w-4 inline mr-1 text-pink-600" />
                        Instagram
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="instagram"
                          value={formData.socialMedia.instagram}
                          onChange={handleSocialMediaChange}
                          placeholder="username"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                        />
                      ) : (
                        formData.socialMedia.instagram ? (
                          <a href={`https://instagram.com/${formData.socialMedia.instagram}`} 
                             target="_blank" rel="noopener noreferrer"
                             className="text-pink-600 hover:underline">
                            {formData.socialMedia.instagram}
                          </a>
                        ) : (
                          <span className="text-gray-400 italic">Not provided</span>
                        )
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <SafeIcon icon={FiLinkedin} className="h-4 w-4 inline mr-1 text-blue-700" />
                        LinkedIn
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="linkedin"
                          value={formData.socialMedia.linkedin}
                          onChange={handleSocialMediaChange}
                          placeholder="username"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                        />
                      ) : (
                        formData.socialMedia.linkedin ? (
                          <a href={`https://linkedin.com/in/${formData.socialMedia.linkedin}`} 
                             target="_blank" rel="noopener noreferrer"
                             className="text-blue-700 hover:underline">
                            {formData.socialMedia.linkedin}
                          </a>
                        ) : (
                          <span className="text-gray-400 italic">Not provided</span>
                        )
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <SafeIcon icon={FiYoutube} className="h-4 w-4 inline mr-1 text-red-600" />
                        YouTube
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="youtube"
                          value={formData.socialMedia.youtube}
                          onChange={handleSocialMediaChange}
                          placeholder="channel name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                        />
                      ) : (
                        formData.socialMedia.youtube ? (
                          <a href={`https://youtube.com/c/${formData.socialMedia.youtube}`} 
                             target="_blank" rel="noopener noreferrer"
                             className="text-red-600 hover:underline">
                            {formData.socialMedia.youtube}
                          </a>
                        ) : (
                          <span className="text-gray-400 italic">Not provided</span>
                        )
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <SafeIcon icon={FiGlobe} className="h-4 w-4 inline mr-1 text-gray-700" />
                        Website
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="website"
                          value={formData.socialMedia.website}
                          onChange={handleSocialMediaChange}
                          placeholder="https://yourwebsite.com"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                        />
                      ) : (
                        formData.socialMedia.website ? (
                          <a href={formData.socialMedia.website} 
                             target="_blank" rel="noopener noreferrer"
                             className="text-blue-600 hover:underline">
                            {formData.socialMedia.website}
                          </a>
                        ) : (
                          <span className="text-gray-400 italic">Not provided</span>
                        )
                      )}
                    </div>
                  </div>
                </motion.div>

                {/* Interests */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="bg-white rounded-lg shadow-md p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-patriot-navy">Republican Interests & Causes</h2>
                    {isEditing && (
                      <button
                        onClick={addInterest}
                        className="text-patriot-red hover:text-patriot-red-dark text-sm font-medium"
                      >
                        + Add Interest
                      </button>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {formData.interests.map((interest, index) => (
                      <span key={index} className="inline-flex items-center px-3 py-1 bg-patriot-red bg-opacity-10 text-patriot-red rounded-full text-sm">
                        {interest}
                        {isEditing && (
                          <button
                            onClick={() => removeInterest(interest)}
                            className="ml-2 text-patriot-red hover:text-patriot-red-dark"
                          >
                            <SafeIcon icon={FiX} className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                  
                  {isEditing && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Available Republican Interests:</h3>
                      <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                        {availableInterests.filter(interest => !formData.interests.includes(interest)).map((interest, index) => (
                          <button
                            key={index}
                            onClick={() => setFormData({ ...formData, interests: [...formData.interests, interest] })}
                            className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs hover:bg-patriot-red hover:text-white transition-colors duration-200"
                          >
                            + {interest}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              </>
            )}

            {/* Achievements Tab */}
            {activeTab === 'achievements' && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <h2 className="text-xl font-semibold text-patriot-navy mb-4">Patriot Achievements</h2>
                
                <div className="flex items-center justify-between mb-6 border-b border-gray-200 pb-4">
                  <div>
                    <span className="text-lg font-bold text-patriot-navy">{achievements.length} Achievements</span>
                    <div className="text-sm text-gray-500">
                      {achievements.filter(a => a.level === a.maxLevel).length} mastered • 
                      {achievements.filter(a => a.level < a.maxLevel).length} in progress
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-500 mr-3">Achievement Score:</span>
                    <span className="text-lg font-bold text-patriot-red">
                      {achievements.reduce((sum, a) => sum + a.level, 0)} / {achievements.reduce((sum, a) => sum + a.maxLevel, 0)}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {achievements.map((achievement) => (
                    <div
                      key={achievement.id}
                      className={`p-4 border rounded-lg transition-all duration-300 hover:shadow-md ${
                        achievement.level === achievement.maxLevel
                          ? 'border-patriot-gold bg-patriot-gold bg-opacity-5'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center mb-3">
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            achievement.level === achievement.maxLevel
                              ? 'bg-patriot-gold bg-opacity-20'
                              : 'bg-gray-100'
                          }`}
                        >
                          <SafeIcon icon={achievement.icon} className={`h-6 w-6 ${achievement.color}`} />
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <h3 className="font-semibold text-gray-900">{achievement.title}</h3>
                            <span
                              className={`ml-2 px-2 py-0.5 text-xs font-bold rounded-full ${getBadgeStyle(
                                achievement.level,
                                achievement.maxLevel
                              )}`}
                            >
                              Level {achievement.level}
                            </span>
                            {achievement.featured && (
                              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-patriot-red text-white rounded-full">
                                Featured
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{achievement.description}</p>
                          <div className="text-xs text-gray-500 mt-1">Earned on {formatDate(achievement.date)}</div>
                        </div>
                      </div>
                      <div className="ml-16">
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${
                              achievement.level === achievement.maxLevel ? 'bg-patriot-gold' : 'bg-patriot-red'
                            }`}
                            style={{ width: `${(achievement.level / achievement.maxLevel) * 100}%` }}
                          ></div>
                        </div>
                        <div className="flex justify-between mt-1 text-xs text-gray-500">
                          <span>Level {achievement.level}</span>
                          <span>Level {achievement.maxLevel}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Activity Tab */}
            {activeTab === 'activity' && (
              <>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="bg-white rounded-lg shadow-md p-6"
                >
                  <h2 className="text-xl font-semibold text-patriot-navy mb-6">Republican Activity</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {activityStats.map((stat, index) => (
                      <div key={index} className="border border-gray-100 rounded-lg p-4 hover:border-patriot-red transition-colors duration-200">
                        <div className="flex items-center mb-2">
                          <div className="bg-patriot-red bg-opacity-10 p-2 rounded-full mr-3">
                            <SafeIcon icon={stat.icon} className="h-5 w-5 text-patriot-red" />
                          </div>
                          <div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">{stat.label}</span>
                              <span className="font-medium">{stat.value}/{stat.total}</span>
                            </div>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className={`h-2 rounded-full ${
                              stat.value / stat.total > 0.7 ? 'bg-patriot-gold' : 'bg-patriot-red'
                            }`}
                            style={{ width: `${(stat.value / stat.total) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Overall Republican Engagement Score</span>
                      <div className="flex items-center">
                        <span className="text-2xl font-bold text-patriot-navy mr-3">A+</span>
                        <span className="text-sm text-patriot-red font-medium">Top 5%</span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="bg-white rounded-lg shadow-md p-6"
                >
                  <h2 className="text-xl font-semibold text-patriot-navy mb-4">Recently Attended Events</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {recentEvents.map(event => (
                      <div key={event.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-all duration-200">
                        <img 
                          src={event.image} 
                          alt={event.title}
                          className="w-full h-32 object-cover" 
                        />
                        <div className="p-3">
                          <h3 className="font-medium text-patriot-navy">{event.title}</h3>
                          <div className="text-sm text-gray-600 mt-1">
                            <div className="flex items-center">
                              <SafeIcon icon={FiCalendar} className="h-3.5 w-3.5 mr-1 text-gray-500" />
                              <span>{formatDate(event.date)}</span>
                            </div>
                            <div className="flex items-center mt-1">
                              <SafeIcon icon={FiMapPin} className="h-3.5 w-3.5 mr-1 text-gray-500" />
                              <span>{event.location}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </>
            )}

            {/* Groups Tab */}
            {activeTab === 'groups' && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-patriot-navy">Your Republican Groups</h2>
                  <button className="flex items-center px-3 py-1 bg-patriot-red text-white rounded-lg hover:bg-patriot-red-dark transition-colors duration-200 text-sm">
                    <SafeIcon icon={FiUsers} className="h-4 w-4 mr-1" />
                    Find Groups
                  </button>
                </div>
                
                <div className="space-y-4">
                  {userGroups.map(group => (
                    <div key={group.id} className="flex items-center border border-gray-200 p-4 rounded-lg hover:border-patriot-red transition-colors duration-200">
                      <img 
                        src={group.image} 
                        alt={group.name}
                        className="w-16 h-16 rounded-lg object-cover" 
                      />
                      <div className="ml-4 flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium text-patriot-navy">{group.name}</h3>
                          <span className={`text-xs px-2 py-1 rounded-full ${group.role === 'Moderator' ? 'bg-patriot-gold text-patriot-navy' : 'bg-gray-100 text-gray-700'}`}>
                            {group.role}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-sm text-gray-600">{group.members} members</span>
                          <span className="text-xs text-gray-500">Last active: {group.lastActive}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-center mt-6">
                  <button className="text-patriot-red hover:text-patriot-red-dark flex items-center font-medium">
                    See All Groups
                    <SafeIcon icon={FiUsers} className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </motion.div>
            )}
          </div>
          
          {/* Sidebar - Only show on Profile tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* Profile Completion */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <h2 className="text-lg font-semibold text-patriot-navy mb-4">Profile Completion</h2>
                <div className="mb-2 flex justify-between">
                  <span className="text-sm text-gray-600">Your profile is</span>
                  <span className="text-sm font-medium">{profileCompletionPercentage}% complete</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className={`h-2.5 rounded-full ${
                      profileCompletionPercentage > 80 ? 'bg-green-500' : 
                      profileCompletionPercentage > 50 ? 'bg-patriot-gold' : 'bg-patriot-red'
                    }`} 
                    style={{ width: `${profileCompletionPercentage}%` }}
                  ></div>
                </div>
                {profileCompletionPercentage < 100 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Complete your profile:</h3>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {!formData.bio && (
                        <li className="flex items-center">
                          <SafeIcon icon={FiX} className="h-3.5 w-3.5 text-patriot-red mr-1.5" />
                          Add a bio
                        </li>
                      )}
                      {!formData.location && (
                        <li className="flex items-center">
                          <SafeIcon icon={FiX} className="h-3.5 w-3.5 text-patriot-red mr-1.5" />
                          Add your location
                        </li>
                      )}
                      {formData.interests.length < 3 && (
                        <li className="flex items-center">
                          <SafeIcon icon={FiX} className="h-3.5 w-3.5 text-patriot-red mr-1.5" />
                          Add at least 3 interests
                        </li>
                      )}
                      {Object.values(formData.socialMedia).filter(Boolean).length < 2 && (
                        <li className="flex items-center">
                          <SafeIcon icon={FiX} className="h-3.5 w-3.5 text-patriot-red mr-1.5" />
                          Connect social media accounts
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </motion.div>

              {/* Quick Actions */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <h2 className="text-lg font-semibold text-patriot-navy mb-4">Quick Actions</h2>
                <div className="space-y-2">
                  <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex items-center">
                    <SafeIcon icon={FiSettings} className="h-4 w-4 mr-3 text-patriot-red" />
                    Account Settings
                  </button>
                  <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex items-center">
                    <SafeIcon icon={FiHeart} className="h-4 w-4 mr-3 text-patriot-red" />
                    Saved Content
                  </button>
                  <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex items-center">
                    <SafeIcon icon={FiActivity} className="h-4 w-4 mr-3 text-patriot-red" />
                    Activity History
                  </button>
                  <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex items-center">
                    <SafeIcon icon={FiFlag} className="h-4 w-4 mr-3 text-patriot-red" />
                    Volunteer Opportunities
                  </button>
                  <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex items-center">
                    <SafeIcon icon={FiTarget} className="h-4 w-4 mr-3 text-patriot-red" />
                    Support Candidates
                  </button>
                </div>
              </motion.div>

              {/* Featured Badge */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="bg-gradient-to-r from-patriot-red to-patriot-navy rounded-lg shadow-md p-6 text-white"
              >
                <div className="flex items-center mb-4">
                  <SafeIcon icon={FiShield} className="h-6 w-6 text-patriot-gold mr-2" />
                  <h2 className="text-lg font-semibold">Featured Achievement</h2>
                </div>
                <h3 className="font-medium mb-2">Constitution Defender</h3>
                <p className="text-sm text-gray-200">
                  You've mastered our Constitution 101 course with a perfect score. Share your knowledge with fellow patriots!
                </p>
                <div className="mt-4 flex justify-center">
                  <div className="p-3 bg-white bg-opacity-10 rounded-full">
                    <SafeIcon icon={FiShield} className="h-10 w-10 text-patriot-gold" />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white border-opacity-20 text-center">
                  <span className="text-xs font-medium text-patriot-gold">TOP 5% OF PATRIOTS</span>
                </div>
              </motion.div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;